// Admin dashboard shared constants + still-mocked fixtures.
//
// Real data now lives at:
//   • /api/admin/opportunities     — review queue rows
//   • /api/admin/sources           — feed sources registry (see SourcesPage)
//   • /api/admin/feed-items        — ingested articles (see FeedMonitorPage)
//   • /api/ai/analyze              — real Claude 3.5 pipeline
//
// The only exports remaining here are:
//   1. UI constants shared by the Review page (status labels/tones).
//   2. The AI Queue demo fixtures — pre-seeded Arabic economic articles the
//      admin can hand-pick to exercise the real /api/ai/analyze pipeline.

export const ADMIN_STATUS = Object.freeze({
  PENDING:   "pending",
  PUBLISHED: "published",
  REJECTED:  "rejected",
});

export const STATUS_LABEL = {
  [ADMIN_STATUS.PENDING]:   "قيد المراجعة",
  [ADMIN_STATUS.PUBLISHED]: "منشورة",
  [ADMIN_STATUS.REJECTED]:  "مرفوضة",
};

export const STATUS_TONE = {
  [ADMIN_STATUS.PENDING]:   "bg-amber-50 text-amber-700 border-amber-200",
  [ADMIN_STATUS.PUBLISHED]: "bg-emerald-50 text-emerald-700 border-emerald-200",
  [ADMIN_STATUS.REJECTED]:  "bg-red-50 text-red-700 border-red-200",
};

/** AI Queue demo fixtures — hand-picked Arabic economic news items that the
 *  admin can send through the real /api/ai/analyze pipeline. Each carries the
 *  full article body used as the pipeline input plus a pre-computed
 *  opportunity preview shown before the analysis runs (offline fallback). */
export const AI_QUEUE_ITEMS = [
  {
    id: "news-01",
    headline: "ساما تُطلق ترخيص المصارف الرقمية لثلاث شركات ناشئة في القطاع المالي",
    source: "البنك المركزي السعودي (ساما)",
    sourceUrl: "https://sama.gov.sa/press/2026-02-10",
    country: "السعودية",
    publishedAt: "2026-02-10 · 09:40ص",
    publishedAtIso: "2026-02-10T09:40:00Z",
    fullText:
      "أعلن البنك المركزي السعودي (ساما) اليوم عن منح تراخيص المصارف الرقمية لثلاث شركات ناشئة سعودية بعد اجتيازها متطلبات الرقابة والالتزام والحوكمة. تشمل الشركات الثلاث: شركة D360 المتخصصة في حلول الدفع الرقمي، وشركة STC Bank الذراع المصرفية لمجموعة الاتصالات السعودية، وشركة Vision Bank التي تركّز على منتجات الادخار والاستثمار الفردي. وأشارت ساما إلى أنّ الطلب على المنتجات المصرفية الرقمية في السعودية ينمو بمعدّل 42٪ سنوياً، وأنّ الشركات الثلاث تخطّط لبدء عملياتها التشغيلية خلال 12 شهراً وتحتاج جولات تمويلية إضافية بحجم يتراوح بين 50 و150 مليون ريال لدعم التوسّع في السوق. كما أوضحت الجهة التنظيمية أنّ التراخيص تشمل تقديم خدمات الحسابات الرقمية، والدفع الفوري، وإدارة الثروات المؤتمتة، مع التزام صارم بمعايير أمن المعلومات ومكافحة غسل الأموال.",
    opportunity: {
      title: "دخول سوق المصارف الرقمية عبر شراكة مع شركة ناشئة مرخّصة حديثاً",
      score: 88,
      type: "فرصة استحواذ",
      industry: "التقنية المالية",
      country: "السعودية",
      executiveSummary:
        "تفتح إصدار ساما لتراخيص المصارف الرقمية الثلاثة نافذةً استثنائية للمستثمرين الاستراتيجيين للدخول عبر حصص أقلية أو استحواذ مبكّر.",
    },
  },
  {
    id: "news-02",
    headline: "موانئ دبي توقّع مذكّرة تفاهم مع مطوّر لوجستي لتطوير 120 ألف متر مربع",
    source: "سلطة موانئ دبي",
    sourceUrl: "https://www.dpworld.com/press/2026-02-10",
    country: "الإمارات",
    publishedAt: "2026-02-10 · 08:15ص",
    publishedAtIso: "2026-02-10T08:15:00Z",
    fullText:
      "وقّعت سلطة موانئ دبي مذكّرة تفاهم مع مطوّر لوجستي إقليمي كبير لتطوير مجمّع لوجستي ذكي على مساحة 120 ألف متر مربع بالقرب من ميناء جبل علي، بتكلفة تقديرية تبلغ 650 مليون درهم. المشروع يستهدف خدمة قطاع التجارة الإلكترونية الإقليمي مع دمج حلول الذكاء الاصطناعي لإدارة المستودعات وسلاسل الإمداد. من المتوقّع أن يستقطب المجمّع من 3 إلى 5 عملاء رئيسيين خلال أوّل 18 شهراً من التشغيل، مع فتح المجال أمام شركات لوجستية إقليمية للاشتراك عبر عقود إيجار طويلة الأجل.",
    opportunity: {
      title: "شراكة تطوير مجمّع لوجستي ذكي بالقرب من ميناء جبل علي",
      score: 87,
      type: "فرصة شراكة",
      industry: "اللوجستيات",
      country: "الإمارات",
      executiveSummary: "…",
    },
  },
  {
    id: "news-03",
    headline: "المكتب الوطني للكهرباء يُعلن مناقصة توريد وتشغيل محطة شمسية بقدرة 300 ميجاواط",
    source: "المكتب الوطني للكهرباء",
    sourceUrl: "https://www.one.org.ma/press/2026-02-09",
    country: "المغرب",
    publishedAt: "2026-02-09 · 04:20م",
    publishedAtIso: "2026-02-09T16:20:00Z",
    fullText:
      "أعلن المكتب الوطني للكهرباء والماء الصالح للشرب في المغرب عن إطلاق مناقصة دولية لتوريد وتركيب وتشغيل محطة طاقة شمسية بقدرة 300 ميجاواط في منطقة ورزازات، ضمن خطة المملكة للوصول إلى 52٪ من الطاقة المتجدّدة بحلول 2030. المناقصة مفتوحة للشركات المؤهّلة تقنياً ومالياً، ومن المتوقّع أن يبلغ الاستثمار الإجمالي 1.4 مليار درهم. توفّر عقود شراء الطاقة (PPA) طويلة الأجل عائداً متوقّعاً بين 11٪ و13٪ خلال 20 سنة تشغيلية. يتوفّر تمويل ميسّر من البنك الأوروبي لإعادة الإعمار والتنمية للشركات التي تشرك شريكاً محلياً بحصة لا تقلّ عن 25٪.",
    opportunity: {
      title: "مناقصة توريد وتشغيل محطة طاقة شمسية بقدرة 300 ميجاواط",
      score: 78,
      type: "فرصة مناقصة",
      industry: "الطاقة المتجدّدة",
      country: "المغرب",
      executiveSummary: "…",
    },
  },
  {
    id: "news-04",
    headline: "الحكومة تُعلن تعيين وزير الصحة الجديد ضمن التعديل الوزاري الأخير",
    source: "الوكالة الرسمية للأنباء",
    sourceUrl: "https://spa.gov.sa/news/appt-2026",
    country: "السعودية",
    publishedAt: "2026-02-08 · 10:00ص",
    publishedAtIso: "2026-02-08T10:00:00Z",
    fullText:
      "أعلن الديوان الملكي اليوم تعيين الدكتور خالد بن أحمد وزيراً للصحة، خلفاً للوزير السابق الذي انتقل إلى منصب استشاري. حضر مراسم التعيين عدد من كبار المسؤولين. لم تُذكر أي تفاصيل حول سياسات جديدة أو استثمارات أو مشاريع صحّية قادمة.",
    opportunity: null,
  },
  {
    id: "news-05",
    headline: "Wamda ترصد تسارع نمو منصّات الزراعة العمودية المدعومة بالذكاء الاصطناعي",
    source: "Wamda",
    sourceUrl: "https://wamda.com/2026/02/vertical-farming-mena",
    country: "إقليمي",
    publishedAt: "2026-02-09 · 10:10ص",
    publishedAtIso: "2026-02-09T10:10:00Z",
    fullText:
      "رصدت منصّة Wamda لبحوث ريادة الأعمال في تقرير جديد نمواً سنوياً بنسبة 63٪ لقطاع الزراعة العمودية في منطقة الشرق الأوسط وشمال إفريقيا، مدعوماً بارتفاع أسعار استيراد الخضروات الطازجة وتزايد التركيز على الأمن الغذائي. يُشير التقرير إلى وجود ما لا يقل عن 14 شركة ناشئة نشِطة في القطاع، أبرزها شركات في السعودية والإمارات ومصر. يُظهر التقرير كذلك اهتماماً متزايداً من مطوّرين عقاريين لتحويل مستودعات قديمة إلى وحدات زراعة عمودية، خصوصاً في المدن الكبرى القريبة من الأسواق الاستهلاكية.",
    opportunity: {
      title: "قطاع صاعد: الزراعة العمودية المدعومة بالذكاء الاصطناعي",
      score: 76,
      type: "قطاع صاعد",
      industry: "الزراعة الذكية",
      country: "إقليمي",
      executiveSummary: "…",
    },
  },
  {
    id: "news-06",
    headline: "هيئة الاتصالات والفضاء والتقنية تفتح تراخيص جديدة لمشغّلي الحوسبة السحابية",
    source: "هيئة الاتصالات والفضاء والتقنية",
    sourceUrl: "https://www.cst.gov.sa/press/2026-02-08",
    country: "السعودية",
    publishedAt: "2026-02-08 · 03:45م",
    publishedAtIso: "2026-02-08T15:45:00Z",
    fullText:
      "أعلنت هيئة الاتصالات والفضاء والتقنية عن فتح باب الترخيص لمشغّلي البنية التحتية للحوسبة السحابية داخل المملكة، ضمن استراتيجية الاقتصاد الرقمي ورؤية 2030. يُتيح الترخيص للشركات المؤهّلة تقديم خدمات IaaS وPaaS وSaaS للجهات الحكومية والقطاع الخاص. الهيئة توقّعت وصول حجم السوق المحلي إلى 2.4 مليار ريال بحلول 2028 مع نموّ سنوي مركّب 19٪. متطلبات الترخيص تشمل الالتزام بمعايير حماية البيانات المحلّية وتوطين نسبة لا تقلّ عن 40٪ من الكوادر التشغيلية.",
    opportunity: {
      title: "دخول سوق الحوسبة السحابية السعودي عبر ترخيص جديد",
      score: 84,
      type: "فرصة ترخيص",
      industry: "التقنية والاتصالات",
      country: "السعودية",
      executiveSummary: "…",
    },
  },
  {
    id: "news-07",
    headline: "شركة تقنية تُعلن أنّ منتجها الجديد سيُحدث ثورة عالمية غير مسبوقة",
    source: "بيان صحفي مموّل",
    sourceUrl: "https://prnewswire.example.com/rev-2026",
    country: "غير محدد",
    publishedAt: "2026-02-07 · 12:00م",
    publishedAtIso: "2026-02-07T12:00:00Z",
    fullText:
      "في بيان صحفي مموّل، أعلنت شركة ناشئة عن إطلاق منتج \"سيغيّر مجرى الصناعة\" و\"سيهيمن على السوق العالمي\" خلال ستة أشهر. لم يُقدّم البيان أي أرقام مالية، ولا اسم فريق الإدارة، ولا شراكات محدّدة، ولا خطة تشغيلية أو مالية. جميع الادّعاءات مبنية على تصريحات المؤسّس فقط، دون أدلّة أو مصادر مستقلّة أو دراسات سوق.",
    opportunity: null,
  },
  {
    id: "news-08",
    headline: "الهيئة العامة للمناطق الاقتصادية العُمانية تُوسّع منطقة الدقم الحرّة",
    source: "الهيئة العامة للمناطق الاقتصادية العُمانية",
    sourceUrl: "https://www.opaz.gov.om/press/2026-02-07",
    country: "عُمان",
    publishedAt: "2026-02-07 · 09:20ص",
    publishedAtIso: "2026-02-07T09:20:00Z",
    fullText:
      "أعلنت الهيئة العامة للمناطق الاقتصادية والمناطق الحرّة العُمانية عن توسّعة منطقة الدقم الاقتصادية الخاصة بمساحة إضافية تبلغ 120 هكتاراً، مع حزمة حوافز تشمل إعفاءً جمركياً كاملاً على المواد الخام والمعدّات، وإعفاءً ضريبياً على الشركات لمدّة 30 سنة قابلة للتجديد. الصناعات المستهدفة تشمل: البتروكيماويات، وتجميع السيارات الكهربائية، ومعالجة المعادن. تتوفّر أراضٍ صناعية جاهزة بأسعار تنافسية، مع إمكانية تأجير طويل الأجل وشراكات مع الحكومة العُمانية.",
    opportunity: {
      title: "توسّع منطقة الدقم الحرّة: فرص صناعية بحوافز جمركية",
      score: 79,
      type: "فرصة استثمار",
      industry: "الصناعة",
      country: "عُمان",
      executiveSummary: "…",
    },
  },
];
